import { ListItem, Skeleton } from '@mui/material';
import useLogout from 'hooks/logout';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { To, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { userIsLoggedIn } from '../../../store/atoms/userAtom';
import { FrontNavLinks } from '../../atoms';
import CategoryFilter from './CategoryFilter';
import { NavCon, SkeletonCon } from './style';

type FrontSideNavType = {
  navs: {
    to: To;
    id?: number;
    name: string;
    logo?: any;
    Icon?: any;
    minimize?: boolean;
  }[];
  isLoading?: boolean;
  title: string;
  signout?: boolean;
  disable?: boolean;
};

// Helper function to get fallback image based on category name
const getFallbackImage = (categoryName: string): string => {
  const name = categoryName.trim().toLowerCase();

  // Active categories
  if (name.includes('grocery') || name.includes('groceries') || name.includes('market')) {
    return '/images/categories/groceries.png';
  }
  if (name.includes('pharmacy') || name.includes('medical') || name.includes('medicine')) {
    return '/images/categories/pharmacy.png';
  }
  // Robust electronics/electrical matching
  if (
    name.includes('electronics') ||
    name.includes('tech') ||
    name.includes('gadget') ||
    name.includes('electrical accessories') ||
    (name.includes('electrical') && name.includes('accessories'))
  ) {
    return '/images/categories/electronics.png';
  }

  // Commented out other categories
  // if (name.includes('agriculture') || name.includes('farming')) {
  //   return '/images/categories/agriculture.png';
  // }
  // if (name.includes('food') || name.includes('restaurant')) {
  //   return '/images/categories/food.png';
  // }
  // if (name.includes('beauty')) {
  //   return '/images/categories/health.png';
  // }
  // if (name.includes('fashion') || name.includes('clothing') || name.includes('apparel')) {
  //   return '/images/categories/fashion.png';
  // }
  // if (name.includes('home') || name.includes('furniture') || name.includes('decor')) {
  //   return '/images/categories/home.png';
  // }
  // if (name.includes('lottery') || name.includes('games') || name.includes('gym')) {
  //   return '/images/categories/lottery.png';
  // }
  // if (name.includes('automotive') || name.includes('car') || name.includes('vehicle')) {
  //   return '/images/categories/auto.png';
  // }
  // if (name.includes('event') || name.includes('occasion') || name.includes('party')) {
  //   return '/images/categories/events.png';
  // }

  // Default fallback
  return '/images/categories/default.png';
};

// Helper function to check if category should be displayed
const shouldDisplayCategory = (categoryName: string): boolean => {
  const name = categoryName.toLowerCase();
  return (
    name.includes('grocery') ||
    name.includes('groceries') ||
    name.includes('supermarket') ||
    name.includes('market') ||
    name.includes('pharmacy') ||
    name.includes('medical') ||
    name.includes('health') ||
    name.includes('medicine') ||
    name.includes('electronics') ||
    name.includes('electrical') ||
    name.includes('accessories')
  );
};

export const FrontSideNav = ({ navs, isLoading, title, signout, disable }: FrontSideNavType) => {
  const [checked, setChecked] = useState(true);
  const [activeNav, setActiveNav] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const location = useLocation();
  const isLogged = useRecoilValue(userIsLoggedIn);
  const logout = useLogout();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isSmallScreen = useMediaQuery({ query: '(max-width: 900px)' });

  const handleImageError = (navName: string) => {
    setImageErrors(prev => ({ ...prev, [navName]: true }));
  };

  const getImageSrc = (logo: string | undefined, name: string) => {
    if (!logo || imageErrors[name]) {
      return getFallbackImage(name);
    }
    return logo;
  };

  const handleChange = () => {
    setChecked(prev => !prev);
  };

  const handleNavClick = (name: string) => {
    setActiveNav(name);

    // Scroll the selected item to the front on mobile
    if (isSmallScreen && scrollContainerRef.current) {
      setTimeout(() => {
        const activeElement = scrollContainerRef.current?.querySelector('.mobile-nav-item--active');
        if (activeElement) {
          activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start'
          });
        }
      }, 100);
    }
  };

  const _logout = async () => {
    logout();
  };

  // Filter navs to only show Grocery and Pharmacy
  const filteredNavs = navs.filter(nav => shouldDisplayCategory(nav.name));

  useEffect(() => {
    if (!filteredNavs?.length) return;

    const url = new URLSearchParams(location.search);
    const categoryId = url.get('category');

    // Method 1: Use category ID from search params (most reliable)
    if (categoryId) {
      const foundNav = filteredNavs.find(nav => nav.id?.toString() === categoryId);
      if (foundNav) {
        setActiveNav(foundNav.name);
        return;
      }
    }

    // Method 2: Fallback - convert URL path to match nav name format
    const pathParts = location.pathname.split('/');
    let lastPart = pathParts.filter(Boolean).pop() || '';
    lastPart = lastPart.replace(/-$/, ''); // Remove trailing dash

    // Convert URL format back to original name format
    const nameFromUrl = lastPart
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const foundNav = filteredNavs.find(nav => nav.name.toLowerCase() === nameFromUrl.toLowerCase());

    if (foundNav) {
      setActiveNav(foundNav.name);
    }
  }, [location.pathname, location.search, filteredNavs]);

  // Reset image errors when navs change
  useEffect(() => {
    setImageErrors({});
  }, [navs]);

  // Reorder navs to put active item first on mobile
  const reorderedNavs = isSmallScreen
    ? [...filteredNavs.filter(nav => nav.name === activeNav), ...filteredNavs.filter(nav => nav.name !== activeNav)]
    : filteredNavs;

  return (
    <NavCon>
      <div className="nav-container">
        {/* Header Section */}
        {!isSmallScreen && (
          <div className="nav-header">
            <h2 className="nav-title">{title}</h2>
            <div className="nav-divider" />
          </div>
        )}

        {/* Navigation Content */}
        {!isLoading ? (
          isSmallScreen ? (
            // Mobile Design - Horizontal Scrollable Cards
            <div className="mobile-nav-container">
              <div className="mobile-nav-scroll" ref={scrollContainerRef}>
                {reorderedNavs?.length > 0 &&
                  reorderedNavs.map(({ name, logo, to }, i) => (
                    <div
                      key={i}
                      onClick={() => handleNavClick(name)}
                      className={`mobile-nav-item ${activeNav === name ? 'mobile-nav-item--active' : 'mobile-nav-item--inactive'
                        }`}>
                      <CategoryFilter
                        name={name}
                        image={getImageSrc(logo, name)}
                        to={to}
                        onImageError={() => handleImageError(name)}
                      />
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            // Desktop Design - Vertical List
            <div className="desktop-nav-container">
              {filteredNavs?.length > 0 &&
                filteredNavs.map(({ to, name, logo }, i) => (
                  <ListItem key={i} disablePadding className="desktop-nav-item">
                    <FrontNavLinks
                      img={getImageSrc(logo, name)}
                      label={name}
                      to={to}
                      isActive={activeNav === name}
                      onImageError={() => handleImageError(name)}
                    />
                  </ListItem>
                ))}
            </div>
          )
        ) : (
          // Loading State
          <div className="loading-container">
            {Array(2)
              .fill(0)
              .map((_, i) => (
                <SkeletonCon key={i} className="loading-item">
                  <Skeleton
                    width={isSmallScreen ? '100px' : '280px'}
                    height={isSmallScreen ? '70px' : '60px'}
                    animation="wave"
                    sx={{
                      borderRadius: isSmallScreen ? '10px' : '8px',
                      backgroundColor: '#f0f0f0'
                    }}
                  />
                </SkeletonCon>
              ))}
          </div>
        )}

        {/* Logout Section */}
        {signout && isLogged && (
          <div className="logout-section">
            <div className="logout-divider" />
            <ListItem disablePadding className="logout-item">
              <button className="logout-button" onClick={_logout}>
                <svg
                  className="logout-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  xmlns="http://www.w3.org/2000/svg">

                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16,17 21,12 16,7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Logout</span>
              </button>
            </ListItem>
          </div>
        )}
      </div>
    </NavCon>
  );
};